import type { Plan } from "../types/user";
import type { ScrapingFrequency } from "../types/keyword";
import { DEFAULT_SCRAPING_FREQUENCY } from "./scraping";

export interface PlanLimits {
  // Credit system - only limit is monthly credits
  includedCredits: number; // Monthly credit allowance
  overageRate: number; // Price per credit over limit (0 for FREE)
  maxOverageCredits: number; // Safety cap to prevent runaway bills

  // Other limits
  maxPlatforms: number; // All plans support 3 platforms
  historyDays: number | null; // Data retention (null = unlimited)
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  FREE: {
    includedCredits: 100,
    overageRate: 0, // No overages on free
    maxOverageCredits: 0, // Hard stop at 100
    maxPlatforms: 3,
    historyDays: 30,
  },
  STARTER: {
    includedCredits: 500,
    overageRate: 0.04, // $0.040 per credit
    maxOverageCredits: 3000, // Max $120 overage
    maxPlatforms: 3,
    historyDays: 90,
  },
  GROWTH: {
    includedCredits: 1500,
    overageRate: 0.035, // $0.035 per credit
    maxOverageCredits: 10000, // Max $350 overage
    maxPlatforms: 3,
    historyDays: 180,
  },
  PRO: {
    includedCredits: 4000,
    overageRate: 0.025, // $0.025 per credit
    maxOverageCredits: 30000, // Max $750 overage
    maxPlatforms: 3,
    historyDays: null, // Unlimited history
  },
  CUSTOM: {
    includedCredits: 0,
    overageRate: 0,
    maxOverageCredits: 0,
    maxPlatforms: 3,
    historyDays: null,
  },
} as const;

export const PLAN_PRICES: Record<Exclude<Plan, "FREE" | "CUSTOM">, number> = {
  STARTER: 19,
  GROWTH: 49,
  PRO: 99,
} as const;

/**
 * Hard input bounds enforced at creation time to prevent abuse and runaway
 * external-scrape spend. Each keyword-platform combination triggers a paid
 * scrape, so an unbounded `keywords x platforms` product is an uncapped
 * spend multiplier. These are absolute abuse ceilings, well above any real
 * customer usage, not per-plan quotas (credits handle per-plan limits).
 */

/**
 * Maximum platform URLs attachable to a single tour. Matches the size of the
 * Platform enum (12) — no legitimate tour can track more platforms than exist.
 */
export const MAX_PLATFORM_URLS_PER_TOUR = 12;

/**
 * Maximum keywords trackable per tour. Real tours use a handful of keywords;
 * this ceiling (40x the largest seeded tour) stops automated abuse while
 * never constraining normal use. Caps worst-case scrape fan-out per tour at
 * MAX_KEYWORDS_PER_TOUR x MAX_PLATFORM_URLS_PER_TOUR combinations.
 */
export const MAX_KEYWORDS_PER_TOUR = 200;

/**
 * Scraping frequencies each plan may request, ordered finest -> coarsest.
 * FREE excludes the sub-daily tiers (EVERY_6_HOURS, HOURLY): an HOURLY schedule
 * is 720 paid checks/month per keyword-platform (~15 credits each), which would
 * exhaust the FREE 100-credit ceiling within hours. DAILY stays on FREE because
 * spend is already hard-capped by that ceiling (maxOverageCredits: 0), so
 * frequency only changes how fast the fixed budget is spent, not the total --
 * and daily data is what makes the free trial reach first value fast.
 */
export const PLAN_ALLOWED_FREQUENCIES: Record<Plan, readonly ScrapingFrequency[]> = {
  FREE: ["DAILY", "WEEKLY"],
  STARTER: ["DAILY", "WEEKLY"],
  GROWTH: ["EVERY_6_HOURS", "DAILY", "WEEKLY"],
  PRO: ["HOURLY", "EVERY_6_HOURS", "DAILY", "WEEKLY"],
  CUSTOM: ["HOURLY", "EVERY_6_HOURS", "DAILY", "WEEKLY"],
} as const;

/**
 * Frequency a plan gets when a client sends none. Prefers the global default
 * (DAILY) when the plan allows it, otherwise falls back to that plan's coarsest
 * allowed frequency, guaranteeing the default is always in the allowed set.
 */
export const PLAN_DEFAULT_FREQUENCY: Record<Plan, ScrapingFrequency> =
  Object.fromEntries(
    (
      Object.entries(PLAN_ALLOWED_FREQUENCIES) as [
        Plan,
        readonly ScrapingFrequency[]
      ][]
    ).map(([plan, allowed]) => [
      plan,
      allowed.includes(DEFAULT_SCRAPING_FREQUENCY)
        ? DEFAULT_SCRAPING_FREQUENCY
        : allowed[allowed.length - 1],
    ])
  ) as Record<Plan, ScrapingFrequency>;

export function buildCustomPlanLimits(overrides: {
  includedCredits?: number | null;
  overageRate?: number | null;
  maxOverageCredits?: number | null;
  historyDays?: number | null;
}): PlanLimits {
  return {
    includedCredits: overrides.includedCredits ?? 0,
    overageRate: overrides.overageRate ?? 0,
    maxOverageCredits: overrides.maxOverageCredits ?? 0,
    maxPlatforms: 3,
    historyDays: overrides.historyDays ?? null,
  };
}
